import { Injectable, inject } from '@angular/core';
import { SpeechRealtimeService } from './speech-realtime.service';
import { BehaviorSubject, Subject } from 'rxjs';

export type RecorderContext = 'speech' | 'speech-realtime' | 'messagebar';

export interface RecorderState {
  readonly isRecording: boolean;
  readonly isPaused: boolean;
  readonly mimeType: string;
  readonly level: number;
  readonly durationMs: number;
  readonly context?: RecorderContext;
}

export interface CompletedRecording {
  readonly blob: Blob;
  readonly context?: RecorderContext;
  readonly durationMs: number;
}

export interface RecordingChunk {
  readonly blob: Blob;
  readonly context?: RecorderContext;
  readonly index: number;
  readonly mimeType: string;
}

export interface StoppedRecording {
  readonly context?: RecorderContext;
  readonly durationMs: number;
  readonly mimeType: string;
  readonly chunkCount: number;
}

type VisualizerMode = 'center' | 'history';

@Injectable({ providedIn: 'root' })
export class AudioRecorderService {
  private static readonly SpeechChunkIntervalMs = 5000;
  private static readonly RealtimeChunkMinimumBytes = 8192;
  private static readonly CenterVisualizerSampleMs = 100;
  private static readonly HistoryVisualizerSampleMs = 200;
  private static readonly HistoryVisualizerWidthPx = 902;
  private static readonly HistoryVisualizerBarWidthPx = 5;
  private static readonly HistoryVisualizerGapPx = 2;
  readonly state$ = new BehaviorSubject<RecorderState>({
    isRecording: false,
    isPaused: false,
    mimeType: 'audio/webm;codecs=opus',
    level: 0,
    durationMs: 0,
  });
  readonly complete$ = new Subject<Blob>();
  readonly completeWithContext$ = new Subject<CompletedRecording>();
  readonly chunk$ = new Subject<RecordingChunk>();
  readonly stoppedWithContext$ = new Subject<StoppedRecording>();
  readonly devices$ = new BehaviorSubject<MediaDeviceInfo[]>([]);

  private static readonly PcmWorkletCode = `
    class SafeGptPcmCaptureProcessor extends AudioWorkletProcessor {
      process(inputs) {
        const channel = inputs[0] && inputs[0][0];
        if (channel && channel.length) {
          const copy = new Float32Array(channel);
          this.port.postMessage(copy, [copy.buffer]);
        }

        return true;
      }
    }

    registerProcessor('safegpt-pcm-capture', SafeGptPcmCaptureProcessor);
  `;

  private readonly speechRealtime = inject(SpeechRealtimeService);
  private mediaRecorder?: MediaRecorder;
  private audioContext?: AudioContext;
  private analyser?: AnalyserNode;
  private source?: MediaStreamAudioSourceNode;
  private pcmWorkletNode?: AudioWorkletNode;
  private pcmWorkletUrl?: string;
  private pcmSilentGain?: GainNode;
  private pcmStreamingSessionId: string | null = null;
  private pcmBufferedSamples: Int16Array[] = [];
  private pcmBufferedSampleCount = 0;
  private levelAnimationFrame?: number;
  private visualizerAnimationFrame?: number;
  private visualizerHost?: HTMLElement;
  private visualizerCanvas?: HTMLCanvasElement;
  private visualizerContext?: CanvasRenderingContext2D | null;
  private resizeObserver?: ResizeObserver;
  private visualizerMode: VisualizerMode = 'center';
  private centerVisualizerLastSampleAt = 0;
  private centerVisualizerCurrent = 0;
  private historyVisualizerLastSampleAt = 0;
  private historyVisualizerCurrent = 0;
  private chunks: Blob[] = [];
  private chunkIndex = 0;
  private mimeType = 'audio/webm;codecs=opus';
  private segmentStartedAt = 0;
  private accumulatedDurationMs = 0;
  private level = 0;
  private context?: RecorderContext;
  private selectedDeviceId: string | null = null;
  private realtimeSessionId: string | null = null;
  private switchingDevice = false;
  private barLevels: number[] = [];

  constructor() {
    globalThis.addEventListener?.('beforeunload', (event) => {
      if (!this.state$.getValue().isRecording) {
        return;
      }

      event.preventDefault();
    Reflect.set(event, 'returnValue', '');
    });
  }

  async start(options?: { context?: RecorderContext; deviceId?: string | null; preserveSession?: boolean }): Promise<void> {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      return;
    }

    const preserveSession = options?.preserveSession ?? false;
    this.context = options?.context ?? this.context;
    this.selectedDeviceId = options?.deviceId ?? this.selectedDeviceId;
    if (!preserveSession) {
      this.realtimeSessionId = options?.context === 'speech-realtime' ? crypto.randomUUID() : null;
      this.chunks = [];
      this.chunkIndex = 0;
      this.accumulatedDurationMs = 0;
    }
    this.segmentStartedAt = Date.now();

    this.mimeType = this.pickMimeType();

    const audioConstraints: MediaTrackConstraints | boolean = this.selectedDeviceId ? { deviceId: { exact: this.selectedDeviceId } } : true;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    await this.refreshDevices();
    const audioWindow = globalThis as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext };
    const AudioContextCtor = audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
    this.audioContext = new AudioContextCtor();
    this.source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.6;
    this.source.connect(this.analyser);
    if (this.context === 'speech-realtime' && this.realtimeSessionId) {
      await this.speechRealtime.startRealtimeTranscription(this.realtimeSessionId);
      await this.startPcmStreaming(this.realtimeSessionId);
    }

    this.mediaRecorder = new MediaRecorder(stream, this.mimeType ? { mimeType: this.mimeType } : undefined);

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        const mimeType = this.normalizeMimeType(this.mediaRecorder?.mimeType || this.mimeType || 'audio/webm');
        if (this.context === 'speech') {
          this.chunk$.next({ blob: event.data, context: this.context, index: this.chunkIndex, mimeType });
          this.chunkIndex += 1;
        } else {
          this.chunks.push(event.data);
        }
      }
    };
    this.mediaRecorder.onstop = () => {
      if (this.switchingDevice) {
        this.switchingDevice = false;
        this.stopPcmStreaming();
        this.cleanupAudioGraph();
        this.mediaRecorder = undefined;
        void this.start({ context: this.context, deviceId: this.selectedDeviceId, preserveSession: true });
        return;
      }

      this.finishRecording();
    };
    this.mediaRecorder.start(this.context === 'speech' ? AudioRecorderService.SpeechChunkIntervalMs : undefined);
    this.startLevelLoop();
    this.startVisualizerLoop();
    this.emitState(true);
  }

  pause(): void {
    if (this.mediaRecorder?.state === 'recording') {
      this.accumulatedDurationMs = this.getDurationMs();
      this.segmentStartedAt = 0;
      this.mediaRecorder.requestData();
      this.mediaRecorder.pause();
      this.stopLevelLoop();
      this.stopVisualizerLoop();
      this.emitState(true);
    }
  }

  resume(): void {
    if (this.mediaRecorder?.state === 'paused') {
      this.segmentStartedAt = Date.now();
      this.mediaRecorder.resume();
      this.startLevelLoop();
      this.startVisualizerLoop();
      this.emitState(true);
    }
  }

  isPaused(): boolean {
    return this.mediaRecorder?.state === 'paused';
  }

  async setRecordingDevice(deviceId: string): Promise<void> {
    this.selectedDeviceId = deviceId;
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      return;
    }

    this.switchingDevice = true;
    this.mediaRecorder.requestData();
    this.mediaRecorder.stop();
    this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
  }

  async refreshDevices(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      this.devices$.next([]);
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    this.devices$.next(devices.filter((device) => device.kind === 'audioinput'));
  }

  async stop(): Promise<void> {
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      return;
    }

    const realtimeSessionId = this.context === 'speech-realtime' ? this.realtimeSessionId : null;
    if (realtimeSessionId) {
      this.stopPcmStreaming();
    }

    this.mediaRecorder.stop();
    this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());

    if (realtimeSessionId) {
      await this.speechRealtime.stopRealtimeTranscription(realtimeSessionId);
    }
  }

  getDurationMs(): number {
    return this.accumulatedDurationMs + (this.segmentStartedAt ? Date.now() - this.segmentStartedAt : 0);
  }

  attachVisualizer(host: HTMLElement, options?: { bars?: number; mode?: VisualizerMode }): void {
    this.visualizerHost = host;
    this.visualizerMode = options?.mode ?? 'center';
    this.barLevels = new Array(Math.max(4, options?.bars ?? 24)).fill(0);
    this.centerVisualizerLastSampleAt = 0;
    this.centerVisualizerCurrent = 0;
    this.historyVisualizerLastSampleAt = 0;
    this.historyVisualizerCurrent = 0;

    const existingCanvas = host.querySelector('canvas');
    existingCanvas?.remove();
    this.visualizerCanvas = document.createElement('canvas');
    this.visualizerCanvas.style.width = '100%';
    this.visualizerCanvas.style.height = '100%';
    host.appendChild(this.visualizerCanvas);
    this.visualizerContext = this.visualizerCanvas.getContext('2d');
    this.resizeVisualizer();
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.resizeVisualizer());
    this.resizeObserver.observe(host);
    this.startVisualizerLoop();
  }

  detachVisualizer(): void {
    if (this.visualizerAnimationFrame) {
      cancelAnimationFrame(this.visualizerAnimationFrame);
      this.visualizerAnimationFrame = undefined;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.visualizerContext = null;
    this.visualizerCanvas = undefined;
    this.visualizerHost = undefined;
    this.centerVisualizerLastSampleAt = 0;
    this.centerVisualizerCurrent = 0;
    this.historyVisualizerLastSampleAt = 0;
    this.historyVisualizerCurrent = 0;
  }

  private pickMimeType(): string {
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', '']
      .find((mimeType) => !mimeType || MediaRecorder.isTypeSupported(mimeType)) ?? '';
  }

  private normalizeMimeType(mimeType: string): string {
    return mimeType.split(';')[0]?.trim() || 'audio/webm';
  }

  private startLevelLoop(): void {
    if (!this.analyser) {
      return;
    }

    const buffer = new Uint8Array(this.analyser.fftSize);
    const tick = () => {
      if (!this.analyser) {
        return;
      }

      this.analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (const value of buffer) {
        const normalized = (value - 128) / 128;
        sum += normalized * normalized;
      }

      this.level = Math.sqrt(sum / buffer.length);
      this.emitState(true);
      this.levelAnimationFrame = requestAnimationFrame(tick);
    };
    tick();
  }

  private stopLevelLoop(): void {
    if (this.levelAnimationFrame) {
      cancelAnimationFrame(this.levelAnimationFrame);
      this.levelAnimationFrame = undefined;
    }
  }

  private startVisualizerLoop(): void {
    if (!this.visualizerCanvas || !this.visualizerContext || !this.analyser) {
      return;
    }

    const frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
    const draw = () => {
      if (!this.visualizerCanvas || !this.visualizerContext || !this.analyser) {
        return;
      }

      const canvas = this.visualizerCanvas;
      const context = this.visualizerContext;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      context.clearRect(0, 0, width, height);
      this.analyser.getByteFrequencyData(frequencyData);

      if (this.visualizerMode === 'history') {
        this.drawHistoryVisualizer(context, frequencyData, width, height);
      } else {
        this.drawCenterVisualizer(context, frequencyData, width, height);
      }

      this.visualizerAnimationFrame = requestAnimationFrame(draw);
    };

    if (this.visualizerAnimationFrame) {
      cancelAnimationFrame(this.visualizerAnimationFrame);
    }
    this.visualizerAnimationFrame = requestAnimationFrame(draw);
  }

  private stopVisualizerLoop(): void {
    if (this.visualizerAnimationFrame) {
      cancelAnimationFrame(this.visualizerAnimationFrame);
      this.visualizerAnimationFrame = undefined;
    }
  }

  private drawCenterVisualizer(context: CanvasRenderingContext2D, frequencyData: Uint8Array, width: number, height: number): void {
    let sum = 0;
    for (const value of frequencyData) {
      sum += value;
    }

    const average = sum / Math.max(1, frequencyData.length);
    const target = Math.min(1, Math.max(0.08, Math.pow(average / 96, 0.62) * 1.15));
    const now = performance.now();
    this.centerVisualizerCurrent = target;

    if (!this.centerVisualizerLastSampleAt || now - this.centerVisualizerLastSampleAt >= AudioRecorderService.CenterVisualizerSampleMs) {
      this.barLevels = [...this.barLevels.slice(1), this.centerVisualizerCurrent];
      this.centerVisualizerLastSampleAt = now;
    }

    const bars = this.barLevels.length;
    const gap = 3;
    const barWidth = Math.max(2, (width - gap * Math.max(0, bars - 1)) / bars);

    for (let index = 0; index < bars; index += 1) {
      const level = this.barLevels[index];
      const barHeight = Math.max(2, Math.pow(level, 0.9) * height);
      const y = (height - barHeight) / 2;
      const radius = Math.min(barWidth / 2, barHeight / 2);
      context.fillStyle = level >= 0.34 ? '#EE2D4A' : '#D9D9D9';
      const x = index * (barWidth + gap);
      this.roundedRect(context, x, y, barWidth, barHeight, radius);
      context.fill();
    }
  }

  private drawHistoryVisualizer(context: CanvasRenderingContext2D, frequencyData: Uint8Array, width: number, height: number): void {
    let sum = 0;
    for (const value of frequencyData) {
      sum += value;
    }

    const average = sum / Math.max(1, frequencyData.length);
    const target = Math.min(1, Math.max(0.08, Math.pow(average / 72, 0.58) * 1.25));
    const now = performance.now();
    this.historyVisualizerCurrent = target;

    if (!this.historyVisualizerLastSampleAt || now - this.historyVisualizerLastSampleAt >= AudioRecorderService.HistoryVisualizerSampleMs) {
      this.barLevels = [...this.barLevels.slice(1), this.historyVisualizerCurrent];
      this.historyVisualizerLastSampleAt = now;
    }

    const bars = this.barLevels.length;
    const gap = AudioRecorderService.HistoryVisualizerGapPx;
    const barWidth = AudioRecorderService.HistoryVisualizerBarWidthPx;
    const visualizerWidth = Math.min(AudioRecorderService.HistoryVisualizerWidthPx, bars * barWidth + (bars - 1) * gap);
    const startX = width - visualizerWidth;
    for (let index = 0; index < bars; index += 1) {
      const level = this.barLevels[index];
      const barHeight = Math.max(2, Math.pow(level, 0.9) * height);
      const x = startX + index * (barWidth + gap);
      const y = (height - barHeight) / 2;
      const radius = barWidth / 2;
      context.fillStyle = '#0B7F61';
      context.globalAlpha = 0.28 + (index / Math.max(1, bars - 1)) * 0.72;
      this.roundedRect(context, x, y, barWidth, barHeight, radius);
      context.fill();
    }
    context.globalAlpha = 1;
  }

  private roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
  }

  private resizeVisualizer(): void {
    if (!this.visualizerCanvas || !this.visualizerHost || !this.visualizerContext) {
      return;
    }

    const rect = this.visualizerHost.getBoundingClientRect();
    const dpr = globalThis.devicePixelRatio || 1;
    this.visualizerCanvas.width = Math.max(1, Math.floor(rect.width * dpr));
    this.visualizerCanvas.height = Math.max(1, Math.floor(rect.height * dpr));
    this.visualizerContext.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private finishRecording(): void {
    const completedDuration = this.getDurationMs();
    this.stopLevelLoop();

    this.cleanupAudioGraph();
    const blob = new Blob(this.chunks, { type: this.mediaRecorder?.mimeType || this.mimeType || 'audio/webm' });
    const completedContext = this.context;
    const completedMimeType = this.normalizeMimeType(this.mediaRecorder?.mimeType || this.mimeType || 'audio/webm');
    const completedChunkCount = this.chunkIndex;
    this.chunks = [];
    this.segmentStartedAt = 0;
    this.accumulatedDurationMs = completedDuration;
    this.emitState(false);
    this.context = undefined;
    this.realtimeSessionId = null;
    if (completedContext === 'speech') {
      this.stoppedWithContext$.next({ context: completedContext, durationMs: completedDuration, mimeType: completedMimeType, chunkCount: completedChunkCount });
    } else if (blob.size > 0 && completedContext !== 'speech-realtime') {
      this.complete$.next(blob);
      this.completeWithContext$.next({ blob, context: completedContext, durationMs: completedDuration });
    }
  }

  private emitState(isRecording: boolean): void {
    const durationMs = this.getDurationMs();

    this.state$.next({
      isRecording,
      isPaused: this.mediaRecorder?.state === 'paused',
      mimeType: this.mediaRecorder?.mimeType || this.mimeType,
      level: this.level,
      durationMs,
      context: this.context,
    });
  }

  private cleanupAudioGraph(): void {
    this.stopPcmStreaming();
    this.source?.disconnect();
    this.analyser?.disconnect();
    void this.audioContext?.close();
    this.source = undefined;
    this.analyser = undefined;
    this.audioContext = undefined;
  }

  private async startPcmStreaming(sessionId: string): Promise<void> {
    if (!this.audioContext || !this.source) {
      return;
    }

    this.stopPcmStreaming();
    this.pcmStreamingSessionId = sessionId;
    this.pcmWorkletUrl = URL.createObjectURL(new Blob([AudioRecorderService.PcmWorkletCode], { type: 'application/javascript' }));
    await this.audioContext.audioWorklet.addModule(this.pcmWorkletUrl);
    this.pcmWorkletNode = new AudioWorkletNode(this.audioContext, 'safegpt-pcm-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.pcmSilentGain = this.audioContext.createGain();
    this.pcmSilentGain.gain.value = 0;
    const sourceSampleRate = this.audioContext.sampleRate;
    this.pcmWorkletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
      if (this.context !== 'speech-realtime' || this.mediaRecorder?.state !== 'recording') {
        return;
      }

      const input = event.data;
      const pcm = this.downsampleTo16BitPcm(input, sourceSampleRate, 16000);
      this.queuePcmChunk(sessionId, pcm);
    };
    this.source.connect(this.pcmWorkletNode);
    this.pcmWorkletNode.connect(this.pcmSilentGain);
    this.pcmSilentGain.connect(this.audioContext.destination);
  }

  private stopPcmStreaming(): void {
    this.flushPcmBuffer();
    this.pcmWorkletNode?.disconnect();
    this.pcmWorkletNode?.port.close();
    this.pcmSilentGain?.disconnect();
    if (this.pcmWorkletUrl) {
      URL.revokeObjectURL(this.pcmWorkletUrl);
    }

    this.pcmWorkletNode = undefined;
    this.pcmWorkletUrl = undefined;
    this.pcmSilentGain = undefined;
    this.pcmStreamingSessionId = null;
  }

  private queuePcmChunk(sessionId: string, pcm: Int16Array): void {
    this.pcmBufferedSamples.push(pcm);
    this.pcmBufferedSampleCount += pcm.length;

    if (this.pcmBufferedSampleCount * Int16Array.BYTES_PER_ELEMENT >= AudioRecorderService.RealtimeChunkMinimumBytes) {
      this.flushPcmBuffer(sessionId);
    }
  }

  private flushPcmBuffer(sessionId = this.pcmStreamingSessionId): void {
    if (!sessionId || this.pcmBufferedSampleCount === 0) {
      return;
    }

    const merged = new Int16Array(this.pcmBufferedSampleCount);
    let offset = 0;
    for (const chunk of this.pcmBufferedSamples) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }

    this.pcmBufferedSamples = [];
    this.pcmBufferedSampleCount = 0;
    this.speechRealtime.sendPcmAudioChunk(sessionId, merged);
  }

  private downsampleTo16BitPcm(input: Float32Array, sourceSampleRate: number, targetSampleRate: number): Int16Array {
    if (sourceSampleRate === targetSampleRate) {
      return this.floatTo16BitPcm(input);
    }

    const ratio = sourceSampleRate / targetSampleRate;
    const outputLength = Math.floor(input.length / ratio);
    const output = new Float32Array(outputLength);
    for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
      const start = Math.floor(outputIndex * ratio);
      const end = Math.min(input.length, Math.floor((outputIndex + 1) * ratio));
      let sum = 0;
      for (let inputIndex = start; inputIndex < end; inputIndex += 1) {
        sum += input[inputIndex];
      }
      output[outputIndex] = sum / Math.max(1, end - start);
    }

    return this.floatTo16BitPcm(output);
  }

  private floatTo16BitPcm(input: Float32Array): Int16Array {
    const output = new Int16Array(input.length);
    for (let index = 0; index < input.length; index += 1) {
      const sample = Math.max(-1, Math.min(1, input[index]));
      output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }

    return output;
  }
}
